import { ICagePosition } from 'src/generated-types'
import { IMainComponentType } from 'src/generated-types/main-component-type'
import { IMaintenanceListItem, IPerformScheduledMaintenances, IScheduledMaintenance } from 'src/generated-types/scheduled-maintenance'
import { ScheduledMaintenanceFilterParams } from 'src/generated-types/scheduled-maintenance-filter'
import { queryClient } from 'src/query-client'
import { scheduledMaintenanceQueryKeys } from 'src/query-keys'
import { useGet, usePatch } from './reactQuery'

const baseUrl = (customerId: EntityId, localityId: EntityId) =>
  `customers/${customerId}/localities/${localityId}/scheduled-maintenances`

export const scheduleMaintenancesEndpoints = {
  getScheduledMaintenanceById(customerId: EntityId, localityId: EntityId, scheduledMaintenanceId: EntityId) {
    return `${baseUrl(customerId, localityId)}/${scheduledMaintenanceId}`
  },
  getScheduledMaintenancesByCage(customerId: EntityId, localityId: EntityId, cagePositionId: EntityId) {
    return `${baseUrl(customerId, localityId)}/positions/${cagePositionId}/scheduled-maintenances-by-cage`
  },
  getScheduledMaintenancesByEquipment(customerId: EntityId, localityId: EntityId, mainComponentTypeId: EntityId) {
    return `${baseUrl(
      customerId,
      localityId,
    )}/main-component-types/${mainComponentTypeId}/scheduled-maintenances-by-equipment`
  },
  getLocalityScheduledMaintenances(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId, localityId)}/locality-scheduled-maintenances`
  },
  getCagePositions(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId, localityId)}/get-positions-for-group-by-cage`
  },
  getMainComponentTypes(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId, localityId)}/get-main-component-types-for-group-by-equipment`
  },
  performScheduledMaintenances(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId, localityId)}/perform-scheduled-maintenances`
  },
  skipScheduledMaintenances(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId, localityId)}/skip-scheduled-maintenances`
  },
  getEquipmentPerformedMaintenances(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId, localityId)}/get-equipment-perform-history`
  },
  getLocalityPerformedMaintenances(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId, localityId)}/get-locality-perform-history`
  },
  getPerformableScheduledMaintenances(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId, localityId)}/performable`
  },
  getMaintenanceList(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId, localityId)}/list`
  },
}

export const invalidateScheduledMaintenanceCache = {
  getScheduledMaintenanceById(customerId: EntityId, localityId: EntityId, scheduledMaintenanceId: EntityId) {
    queryClient.invalidateQueries(
      scheduledMaintenanceQueryKeys.GET_SCHEDULED_MAINTENANCE_BY_ID(customerId, localityId, scheduledMaintenanceId),
    )
  },
  getScheduledMaintenancesByCage(
    customerId: EntityId,
    localityId?: EntityId,
    cagePositionId?: EntityId,
    queryParams?: object,
  ) {
    queryClient.invalidateQueries(
      scheduledMaintenanceQueryKeys.GET_SCHEDULED_MAINTENANCES_BY_CAGE(
        customerId,
        localityId,
        cagePositionId,
        queryParams,
      ),
    )
  },
  getScheduledMaintenancesByEquipment(
    customerId: EntityId,
    localityId?: EntityId,
    mainComponentTypeId?: EntityId,
    queryParams?: object,
  ) {
    queryClient.invalidateQueries(
      scheduledMaintenanceQueryKeys.GET_SCHEDULED_MAINTENANCES_BY_EQUIPMENT(
        customerId,
        localityId,
        mainComponentTypeId,
        queryParams,
      ),
    )
  },
  getLocalityScheduledMaintenances(customerId: EntityId, localityId?: EntityId, queryParams?: object) {
    queryClient.invalidateQueries(
      scheduledMaintenanceQueryKeys.GET_LOCALITY_SCHEDULED_MAINTENANCES(customerId, localityId, queryParams),
    )
  },
  getCagePositions(customerId: EntityId, localityId?: EntityId, queryParams?: object) {
    queryClient.invalidateQueries(scheduledMaintenanceQueryKeys.GET_CAGE_POSITIONS(customerId, localityId, queryParams))
  },
  getMainComponentTypes(customerId: EntityId, localityId?: EntityId, queryParams?: object) {
    queryClient.invalidateQueries(
      scheduledMaintenanceQueryKeys.GET_MAIN_COMPONENT_TYPES(customerId, localityId, queryParams),
    )
  },
  getEquipmentPerformedMaintenances(customerId: EntityId, localityId: EntityId) {
    queryClient.invalidateQueries(
      scheduledMaintenanceQueryKeys.GET_EQUIPMENT_PERFORMED_MAINTENANCES(customerId, localityId),
    )
  },
  getLocalityPerformedMaintenances(customerId: EntityId, localityId: EntityId) {
    queryClient.invalidateQueries(
      scheduledMaintenanceQueryKeys.GET_LOCALITY_PERFORMED_MAINTENANCES(customerId, localityId),
    )
  },
  invalidateAllScheduledMaintenances(
    customerId: EntityId,
    localityId?: EntityId,
    cagePositionId?: EntityId,
    mainComponentTypeId?: EntityId,
    queryParams?: object,
  ) {
    invalidateScheduledMaintenanceCache.getScheduledMaintenancesByCage(
      customerId,
      localityId,
      cagePositionId,
      queryParams,
    )
    invalidateScheduledMaintenanceCache.getScheduledMaintenancesByEquipment(
      customerId,
      localityId,
      mainComponentTypeId,
      queryParams,
    )
    invalidateScheduledMaintenanceCache.getCagePositions(customerId, localityId, queryParams)
    invalidateScheduledMaintenanceCache.getMainComponentTypes(customerId, localityId, queryParams)
    invalidateScheduledMaintenanceCache.getMaintenanceList(customerId, localityId, queryParams)
  },
  getMaintenanceList(customerId: EntityId, localityId?: EntityId, queryParams?: object) {
    queryClient.invalidateQueries(
      scheduledMaintenanceQueryKeys.GET_MAINTENANCE_LIST(customerId, localityId, queryParams),
    )
  },
}

export const useGetScheduledMaintenanceById = (
  customerId: EntityId,
  localityId: EntityId,
  scheduledMaintenanceId: EntityId,
) =>
  useGet<IScheduledMaintenance>(
    scheduleMaintenancesEndpoints.getScheduledMaintenanceById(customerId, localityId, scheduledMaintenanceId),
    {},
    {
      queryKey: scheduledMaintenanceQueryKeys.GET_SCHEDULED_MAINTENANCE_BY_ID(
        customerId,
        localityId,
        scheduledMaintenanceId,
      ),
    },
  )

export const useGetScheduledMaintenancesByCage = (
  customerId: EntityId,
  localityId: EntityId,
  cagePositionId: EntityId,
  onlyDaily = false,
  filterParams?: ScheduledMaintenanceFilterParams,
  options?: any,
) =>
  useGet<Array<IScheduledMaintenance>>(
    scheduleMaintenancesEndpoints.getScheduledMaintenancesByCage(customerId, localityId, cagePositionId),
    { onlyDaily, ...filterParams },
    {
      queryKey: scheduledMaintenanceQueryKeys.GET_SCHEDULED_MAINTENANCES_BY_CAGE(
        customerId,
        localityId,
        cagePositionId,
        { onlyDaily, ...filterParams },
      ),
      ...options,
    },
  )

export const useGetScheduledMaintenancesByEquipment = (
  customerId: EntityId,
  localityId: EntityId,
  mainComponentTypeId: EntityId,
  onlyDaily = false,
  filterParams?: ScheduledMaintenanceFilterParams,
  options?: any,
) =>
  useGet<Array<IScheduledMaintenance>>(
    scheduleMaintenancesEndpoints.getScheduledMaintenancesByEquipment(customerId, localityId, mainComponentTypeId),
    { onlyDaily, ...filterParams },
    {
      queryKey: scheduledMaintenanceQueryKeys.GET_SCHEDULED_MAINTENANCES_BY_EQUIPMENT(
        customerId,
        localityId,
        mainComponentTypeId,
        { onlyDaily, ...filterParams },
      ),
      ...options,
    },
  )

export const useGetLocalityScheduledMaintenances = (customerId: EntityId, localityId: EntityId, onlyDaily = false) =>
  useGet<Array<IScheduledMaintenance>>(
    scheduleMaintenancesEndpoints.getLocalityScheduledMaintenances(customerId, localityId),
    { onlyDaily },
    {
      queryKey: scheduledMaintenanceQueryKeys.GET_LOCALITY_SCHEDULED_MAINTENANCES(customerId, localityId, {
        onlyDaily,
      }),
    },
  )

export const useGetCagePositions = (
  customerId: EntityId,
  localityId: EntityId,
  onlyDaily = false,
  filterParams?: ScheduledMaintenanceFilterParams,
  options?: any,
) =>
  useGet<Array<ICagePosition>>(
    scheduleMaintenancesEndpoints.getCagePositions(customerId, localityId),
    { onlyDaily, ...filterParams },
    {
      queryKey: scheduledMaintenanceQueryKeys.GET_CAGE_POSITIONS(customerId, localityId, {
        onlyDaily,
        ...filterParams,
      }),
      ...options,
    },
  )

export const useGetMainComponentTypesForGroupBy = (
  customerId: EntityId,
  localityId: EntityId,
  onlyDaily = false,
  filterParams?: ScheduledMaintenanceFilterParams,
  options?: any,
) =>
  useGet<Array<IMainComponentType>>(
    scheduleMaintenancesEndpoints.getMainComponentTypes(customerId, localityId),
    { onlyDaily, ...filterParams },
    {
      queryKey: scheduledMaintenanceQueryKeys.GET_MAIN_COMPONENT_TYPES(customerId, localityId, {
        onlyDaily,
        ...filterParams,
      }),
      ...options,
    },
  )

export const usePerformScheduledMaintenances = (customerId: EntityId, localityId: EntityId) =>
  usePatch<IPerformScheduledMaintenances, any>(
    scheduleMaintenancesEndpoints.performScheduledMaintenances(customerId, localityId),
  )

export const useSkipScheduledMaintenances = (customerId: EntityId, localityId: EntityId) =>
  usePatch<IPerformScheduledMaintenances, any>(
    scheduleMaintenancesEndpoints.skipScheduledMaintenances(customerId, localityId),
  )

export const useGetEquipmentPerformedMaintenances = (customerId: EntityId, localityId: EntityId, options?: any) =>
  useGet<Array<IMainComponentType>>(
    scheduleMaintenancesEndpoints.getEquipmentPerformedMaintenances(customerId, localityId),
    {},
    {
      queryKey: scheduledMaintenanceQueryKeys.GET_EQUIPMENT_PERFORMED_MAINTENANCES(customerId, localityId),
      ...options,
    },
  )

export const useGetLocalityPerformedMaintenances = (customerId: EntityId, localityId: EntityId, options?: any) =>
  useGet<Array<IMainComponentType>>(
    scheduleMaintenancesEndpoints.getLocalityPerformedMaintenances(customerId, localityId),
    {},
    {
      queryKey: scheduledMaintenanceQueryKeys.GET_LOCALITY_PERFORMED_MAINTENANCES(customerId, localityId),
      ...options,
    },
  )

export const useGetPerformableScheduledMaintenances = (customerId: EntityId, localityId: EntityId, ids?: number[], options?: any) =>
  useGet<Array<IScheduledMaintenance>>(
    scheduleMaintenancesEndpoints.getPerformableScheduledMaintenances(customerId, localityId),
    { ids },
    {
      queryKey: scheduledMaintenanceQueryKeys.GET_PERFORMABLE_SCHEDULED_MAINTENANCES(customerId, localityId, ids),
      enabled: !!ids && ids.length > 0,
      ...options,
    },
  )

export const useGetMaintenanceList = (
  customerId: EntityId,
  localityId: EntityId,
  filterParams?: ScheduledMaintenanceFilterParams,
  options?: any,
) =>
  useGet<Array<IMaintenanceListItem>>(
    scheduleMaintenancesEndpoints.getMaintenanceList(customerId, localityId),
    { ...filterParams },
    {
      queryKey: scheduledMaintenanceQueryKeys.GET_MAINTENANCE_LIST(customerId, localityId, { ...filterParams }),
      ...options,
    },
  )
